import { z } from "zod";

// ============= Metric Schemas =============

const tokenMetricSchema = z.object({
  type: z.literal("tokens"),
  subtype: z.enum(["input", "output", "cache_read", "cache_write", "reasoning", "total"]),
  unit: z.literal("token"),
  per: z.enum(["unit", "thousand", "million", "billion"]).optional(),
});

const timeMetricSchema = z.object({
  type: z.literal("time"),
  subtype: z.enum(["transcription", "tts", "realtime", "processing"]),
  unit: z.enum(["second", "minute", "hour"]),
  per: z.enum(["unit", "thousand", "million"]).optional(),
});

const characterMetricSchema = z.object({
  type: z.literal("characters"),
  subtype: z.literal("total"),
  unit: z.literal("character"),
  per: z.enum(["unit", "thousand", "million"]).optional(),
});

const requestMetricSchema = z.object({
  type: z.literal("requests"),
  subtype: z.enum(["api", "completion", "embedding", "moderation"]).optional(),
  unit: z.literal("request"),
  per: z.literal("unit"),
});

const imageMetricSchema = z.object({
  type: z.literal("images"),
  subtype: z.enum(["generation", "analysis", "edit"]),
  size: z.enum(["256x256", "512x512", "1024x1024", "1024x1792", "1792x1024"]).optional(),
  unit: z.literal("image"),
  quality: z.enum(["standard", "hd"]).optional(),
  per: z.literal("unit"),
});

const byteMetricSchema = z.object({
  type: z.literal("bytes"),
  subtype: z.enum(["storage", "transfer", "bandwidth", "tts"]),
  unit: z.enum(["byte", "kilobyte", "megabyte", "gigabyte"]),
  per: z.enum(["unit", "kilobyte", "megabyte", "gigabyte", "million"]).optional(),
});

export const metricSchema = z.discriminatedUnion("type", [
  tokenMetricSchema,
  timeMetricSchema,
  characterMetricSchema,
  requestMetricSchema,
  imageMetricSchema,
  byteMetricSchema,
]).superRefine((metric, context) => {
  if (metric.type !== "bytes") return;

  const isTtsContract = metric.subtype === "tts"
    && metric.unit === "byte"
    && metric.per === "million";
  const isDataContract = metric.subtype !== "tts" && metric.per !== "million";
  if (!isTtsContract && !isDataContract) {
    context.addIssue({
      code: "custom",
      message: "TTS byte metrics must use byte per million; data byte metrics cannot use per million",
      path: ["per"],
    });
  }
});

export type Metric = z.infer<typeof metricSchema>;

// ============= Calculation Schemas =============

const linearCalculationSchema = z.object({
  method: z.literal("linear"),
  minimum: z.number().optional(),
});

const steppedCalculationSchema = z.object({
  method: z.literal("stepped"),
  blockSize: z.number(),
  rounding: z.enum(["up", "down", "nearest"]),
  minimum: z.number().optional(),
  priceAppliesTo: z.enum(["block", "unit"]).optional(),
});

const tieredCalculationSchema = z.object({
  method: z.literal("tiered"),
  tiers: z.array(
    z.object({
      from: z.number(),
      to: z.number().nullable(),
      pricePerUnit: z.number(),
    }),
  ),
  minimum: z.number().optional(),
});

const fixedCalculationSchema = z.object({
  method: z.literal("fixed"),
  minimum: z.number().optional(),
});

export const calculationSchema = z.discriminatedUnion("method", [
  linearCalculationSchema,
  steppedCalculationSchema,
  tieredCalculationSchema,
  fixedCalculationSchema,
]);

export type Calculation = z.infer<typeof calculationSchema>;

// ============= Pricing Rule Schema =============

export const ruleNameSchema = z.enum([
  "input_token",
  "output_token",
  "cache_token",
  "cache_read_token",
  "cache_write_token",
  "total_token",
  "reasoning_token",
  "transcription",
  "tts_time",
  "tts_char",
  "tts_byte",
  "realtime_time",
  "character",
  "request",
  "image_edit",
  "storage_byte",
  "embedding_token",
  "insight",
  "anonymization",
  "llm_boost",
  "audio_enhancement",
  "classification",
  "processing",
  "processing_time",
  "chat",
  "voice",
  "web_search",
  "code_execution",
]);

export type RuleName = z.infer<typeof ruleNameSchema>;

const utcWindowSchema = z.object({
  days: z.array(z.number().int().min(1).max(7)).min(1),
  from: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
  to: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
}).refine(window => window.from < window.to);

const priceConditionSchema = z.object({
  name: z.string().regex(/^[a-z][a-z0-9_]{0,31}$/),
  when: z.union([
    z.strictObject({ prompt_tokens_gt: z.number().int().positive() }),
    z.strictObject({ utc_windows: z.array(utcWindowSchema).min(1) }),
  ]),
  selling_price: z.number(),
});

// Conditions are an extensible API boundary. Drop only invalid entries so a
// future condition shape cannot prevent the page (or valid siblings) rendering.
const priceConditionsSchema = z.preprocess((value) => {
  if (!Array.isArray(value)) return undefined;
  const conditions = value.flatMap((entry: unknown) => {
    const parsed = priceConditionSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
  return conditions.length ? conditions : undefined;
}, z.array(priceConditionSchema).optional());

export const pricingRuleSchema = z.object({
  name: ruleNameSchema,
  description: z.string().optional(),
  metric: metricSchema,
  selling_price: z.number(),
  calculation: calculationSchema,
  conditions: priceConditionsSchema,
  modifiers: z
    .array(
      z.object({
        type: z.enum(["surcharge", "multiplier", "override"]),
        value: z.number(),
        condition: z.string().optional(),
      }),
    )
    .optional(),
});

export type PricingRule = z.infer<typeof pricingRuleSchema>;

// Service pricing is intentionally extensible: unlike model pricing, service
// metrics and calculations can introduce domain-specific units and snake_case
// parameters without requiring a client release first.
export const servicePricingRuleSchema = z.object({
  name: z.string().regex(/^[a-z][a-z0-9_]*$/),
  description: z.string().optional(),
  metric: z.object({
    type: z.string(),
    unit: z.string(),
    per: z.string().optional(),
    subtype: z.string().optional(),
  }).loose(),
  selling_price: z.number(),
  calculation: z.object({
    method: z.string(),
  }).loose(),
  modifiers: z.array(z.object({
    type: z.string(),
    value: z.number(),
    condition: z.string().optional(),
  }).loose()).optional(),
}).loose();

export type ServicePricingRule = z.infer<typeof servicePricingRuleSchema>;

// ============= Rate Limit Schema =============

export const rateLimitSchema = z.object({
  type: z.string(),
  limit: z.number(),
});

export type RateLimit = z.infer<typeof rateLimitSchema>;

// ============= Main Pricing Schema =============

export const pricingSchema = z.object({
  name: z.string(),
  currency: z.string(),

  services: z.array(
    z.object({
      service_name: z.string().regex(/^[a-z][a-z0-9_]*$/),
      pricing_rules: z.array(servicePricingRuleSchema),
    }),
  ),

  models: z.array(
    z.object({
      name: z.string(),
      type: z.string(),
      priority: z.number(),
      provider: z.string().optional(),
      deprecated: z.boolean().optional(),
      // AI-1198: WebSocket-only model; default(false) keeps an older server's
      // payload parseable (field absent) without breaking the price screens.
      realtime: z.boolean().default(false),
      rate_limit: z.array(rateLimitSchema),
      pricing_rules: z.array(pricingRuleSchema),
    }),
  ),
});

export type Pricing = z.infer<typeof pricingSchema>;

// Array of pricing plans (for public pricing endpoint)
export const pricingPlansSchema = z.array(pricingSchema);
export type PricingPlans = z.infer<typeof pricingPlansSchema>;
